import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ColumnVisibilityToggle from '../../../../components/v2/ColumnVisibilityToggle.jsx';
import FilterPopover from '../../../../components/v2/FilterPopover.jsx';
import ActiveFilterBar from '../../../../components/v2/ActiveFilterBar.jsx';
import { exportCareflowCsv } from '../../../../services/csvExport.js';
import usePersistedColumns from '../../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';
import NoneActionCell from '../../../../components/v2/NoneActionCell.jsx';
import Pagination from '../../../../components/v2/Pagination.jsx';
import { anchoredPopoverStyle } from '../../../../utils/popoverPosition.js';
import RowActionsMenu from '../../../../components/v2/RowActionsMenu.jsx';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';
import { formatPhone } from '../../../../libs.jsx';

// ---------------------------------------------------------------------------
// Time formatting — 12-hour AM/PM
// ---------------------------------------------------------------------------
const formatTime12h = (seconds) => {
    if (seconds == null) return '';
    const hours24 = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const period = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
    return `${hours12}:${String(mins).padStart(2, '0')} ${period}`;
};

// ---------------------------------------------------------------------------
// Output badge hover popover — config + formatting helpers
// ---------------------------------------------------------------------------
// Per output column: which row.outputs key it reads, what the reference list
// represents, the Message-After unit, and whether repeat applies. Offsite/mute
// are intentionally absent — no recipient list to preview.
const OUTPUT_POPOVER_CONFIG = {
    mobile_display: { outputKey: 'palmobile',     kind: 'groups', timeUnit: 'minutes', hasRepeat: true,  listTitle: 'Groups' },
    text_display:   { outputKey: 'textmessages',  kind: 'phones', timeUnit: 'minutes', hasRepeat: false, listTitle: 'Phone Numbers' },
    voice_display:  { outputKey: 'voicemessages', kind: 'phones', timeUnit: 'minutes', hasRepeat: false, listTitle: 'Phone Numbers' },
    email_display:  { outputKey: 'email',         kind: 'emails', timeUnit: 'minutes', hasRepeat: false, listTitle: 'Emails' },
    pager_display:  { outputKey: 'pager',         kind: 'groups', timeUnit: 'seconds', hasRepeat: true,  listTitle: 'Groups' },
};

/** event_time (seconds) → "Immediate" / "X min" / "X sec" depending on unit. */
const formatMsgAfter = (seconds, unit) => {
    const s = parseInt(seconds, 10) || 0;
    if (s === 0) return 'Immediate';
    if (unit === 'seconds') return `${s} sec`;
    return `${Math.round(s / 60)} min`;
};

/** repeat_enabled + next_event_time (seconds) → "Every X min" / "Off". */
const formatRepeat = (output) => {
    const next = parseInt(output?.next_event_time, 10) || 0;
    if (!output?.repeat_enabled || next <= 0) return 'Off';
    return `Every ${Math.round(next / 60)} min`;
};

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------
const CORE_COLUMN_DEFS = [
    { key: 'careflow_description', label: 'Description', sortable: true, filterable: false, required: true },
    { key: 'status_display', label: 'Status', sortable: true, filterable: true, required: false },
    { key: 'time_display', label: 'Active Time', sortable: true, filterable: false, required: false },
    { key: 'mobile_display', label: 'Mobile', sortable: true, filterable: true, required: false },
    { key: 'text_display', label: 'Text', sortable: true, filterable: true, required: false },
    { key: 'voice_display', label: 'Voice', sortable: true, filterable: true, required: false },
    { key: 'email_display', label: 'Email', sortable: true, filterable: true, required: false },
    { key: 'pager_display', label: 'Pager', sortable: true, filterable: true, required: false },
    { key: 'offsite_display', label: 'Offsite', sortable: true, filterable: true, required: false },
    { key: 'mute_display', label: 'Mute', sortable: true, filterable: true, required: false },
];

const ALERT_CATEGORY_COL = { key: 'event_category_display', label: 'Category', sortable: true, filterable: true, required: false };

// Shared column key for the per-subtab references column (Devices / Resident
// Groups / Area Groups). Filtering on it is a membership test against the row's
// _refObjects list, not the scalar count — see referenceFilterValue + the
// filter pipeline below.
const REFERENCES_COLUMN_KEY = 'references_count';

const REFERENCES_COLS = {
    device:   { key: REFERENCES_COLUMN_KEY, label: 'Devices', sortable: true, filterable: true, required: false },
    resident: { key: REFERENCES_COLUMN_KEY, label: 'Resident Groups', sortable: true, filterable: true, required: false },
    area:     { key: REFERENCES_COLUMN_KEY, label: 'Area Groups', sortable: true, filterable: true, required: false },
};

// Output columns are gated by global Output Settings — when an output is off
// for the campus, the column disappears from the table, the columns toggle,
// and the CSV export. Keep this map in sync with CAREFLOW_HEADERS in csvExport.js.
const OUTPUT_COLUMN_FLAG_KEYS = {
    mobile_display: 'app_active',
    text_display: 'text_messaging_active',
    voice_display: 'voice_active',
    email_display: 'email_active',
    pager_display: 'pager_active',
    offsite_display: 'offsite_active',
};

// Maps the output column key to the assign-handler prop name. Clicking an
// On badge edits the existing assignment; clicking an Off badge opens the
// same drawer and the parent's save handler auto-enables the master flag.
const OUTPUT_BADGE_HANDLER_KEYS = {
    mobile_display: 'onAssignMobileGroups',
    text_display: 'onAssignTextPhones',
    voice_display: 'onAssignVoicePhones',
    email_display: 'onAssignEmails',
    pager_display: 'onAssignPagerGroups',
    offsite_display: 'onSetOffsiteDelay',
};

function isOutputColumnEnabled(columnKey, campusSettings) {
    const flagKey = OUTPUT_COLUMN_FLAG_KEYS[columnKey];
    if (!flagKey) return true;
    return campusSettings?.[flagKey] === 1;
}

function buildColumnDefs(subTabKey, campusSettings) {
    const cols = [...CORE_COLUMN_DEFS];
    if (subTabKey === 'alert') {
        cols.splice(1, 0, ALERT_CATEGORY_COL);
    } else if (REFERENCES_COLS[subTabKey]) {
        cols.splice(1, 0, REFERENCES_COLS[subTabKey]);
    }
    return cols.filter((col) => isOutputColumnEnabled(col.key, campusSettings));
}

// ---------------------------------------------------------------------------
// References popover cell — badge with hover list for devices, resident
// groups, or area groups depending on the subtab.
// ---------------------------------------------------------------------------
/**
 * `descKeys` is joined with " · " into the sub-line, mirroring the edit drawer's
 * device picker (CareflowDrawer.jsx:68 → CareflowEntityPicker.jsx:112) so the
 * same device reads the same way whether you hover the badge or open the drawer.
 *
 * Missing values are dropped rather than rendered as gaps, so an unassigned
 * device degrades to just its serial and one with no placement text shows
 * "Room A · 4749727" — no stray separators either way.
 */
const REF_POPOVER_CONFIG = {
    device:   { icon: 'fe-cpu',   title: 'Devices',         nameKey: 'device_name',         descKeys: ['descriptions', 'area_name', 'manufacture_uuid'] },
    resident: { icon: 'fe-users', title: 'Resident Groups', nameKey: 'resident_group_name', descKeys: ['resident_description'] },
    area:     { icon: 'fe-map',   title: 'Area Groups',     nameKey: 'area_group_name',     descKeys: ['area_group_description'] },
};

/** Sub-line for a popover row: present values only, " · " separated. */
function referenceSubLine(item, descKeys) {
    return (descKeys || [])
        .map((k) => String(item?.[k] ?? '').trim())
        .filter(Boolean)
        .join(' · ');
}

// Maps a reference entity (from row._refObjects) to its filter value/label for
// the references column. Devices show "name (uuid)" but filter effectively by
// the unique manufacture_uuid; resident/area filter by group name. The SAME
// builder feeds both the filter option list and the row membership test so they
// always agree.
function referenceFilterValue(item, subTabKey) {
    if (subTabKey === 'device') {
        const uuid = item?.manufacture_uuid ?? '';
        return item?.device_name ? `${item.device_name} (${uuid})` : String(uuid);
    }
    if (subTabKey === 'resident') return item?.resident_group_name ?? '';
    if (subTabKey === 'area') return item?.area_group_name ?? '';
    return '';
}

const ReferencesPopoverCell = ({ items, subTabKey, row, canEdit, onAssign }) => {
    const [open, setOpen] = useState(false);
    const [popoverStyle, setPopoverStyle] = useState({});
    const ref = useRef(null);
    const hoverTimeout = useRef(null);
    const cfg = REF_POPOVER_CONFIG[subTabKey] || REF_POPOVER_CONFIG.device;
    // When the parent provides an assign handler and the user has manage rights
    // (same gate as the row kebab), the badge click opens the assign drawer.
    // The hover popover preview keeps working in either mode.
    const isClickToAssign = !!onAssign && canEdit;

    const computePosition = () => {
        const style = anchoredPopoverStyle(ref.current, { flipBelow: 200, gap: 4 });
        if (style) setPopoverStyle(style);
    };

    const handleMouseEnter = () => {
        clearTimeout(hoverTimeout.current);
        computePosition();
        setOpen(true);
    };

    const handleMouseLeave = () => {
        hoverTimeout.current = setTimeout(() => setOpen(false), 100);
    };

    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    useEffect(() => () => clearTimeout(hoverTimeout.current), []);

    // Zero state keeps the muted "None" look but carries the badge's action
    // when the user has manage rights (same gate as the row kebab); otherwise
    // it stays plain text. After the hooks, never before: assigning a first item
    // flips this branch on a live row, and an early return above the effects
    // changes the hook count.
    if (!items || items.length === 0) {
        return (
            <NoneActionCell
                className="ap-cell-text--muted"
                onClick={isClickToAssign ? () => onAssign(row) : undefined}
                title={`Click to assign ${(cfg.title || 'items').toLowerCase()}`}
            />
        );
    }

    const handleBadgeClick = () => {
        if (isClickToAssign) {
            setOpen(false);
            onAssign(row);
        } else {
            computePosition();
            setOpen(!open);
        }
    };

    return (
        <div className="ap-device-cell" ref={ref} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <button
                type="button"
                className="ap-device-badge"
                onClick={handleBadgeClick}
                aria-label={isClickToAssign
                    ? `Manage ${items.length} ${cfg.title.toLowerCase()}`
                    : `${items.length} ${cfg.title.toLowerCase()} assigned`}
                title={isClickToAssign ? `Click to manage ${cfg.title.toLowerCase()}` : undefined}
            >
                <i className={`fe ${cfg.icon}`} style={{ fontSize: '12px' }} />
                {items.length}
            </button>
            {open ? (
                <div className="ap-device-popover" style={popoverStyle}>
                    <div className="ap-device-popover__title">{cfg.title} ({items.length})</div>
                    <ul className="ap-device-popover__list">
                        {items.map((item, i) => {
                            const subLine = referenceSubLine(item, cfg.descKeys);
                            return (
                                <li key={item.device_id || item.resident_group_id || item.area_group_id || i} className="ap-device-popover__item">
                                    <span className="ap-device-popover__name">{item[cfg.nameKey] || item.descriptions || '—'}</span>
                                    {subLine ? (
                                        <span className="ap-device-popover__uuid">{subLine}</span>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Output badge hover popover — wraps an "On" output badge and reveals the
// Message-After delay, repeat interval (mobile/pager), and the assigned
// recipients (groups / phone numbers / emails) on hover. Off badges and
// outputs without a config (offsite/mute) render the badge unchanged.
// ---------------------------------------------------------------------------
const OutputBadgePopover = ({ enabled, output, kind, timeUnit, hasRepeat, listTitle, label, groupLookup, children }) => {
    const [open, setOpen] = useState(false);
    const [popoverStyle, setPopoverStyle] = useState({});
    const ref = useRef(null);
    const hoverTimeout = useRef(null);

    const items = useMemo(() => {
        const parts = (output?.reference || '').split(',').map((s) => s.trim()).filter(Boolean);
        if (kind === 'groups') return parts.map((id) => groupLookup.get(String(id)) || `Group ${id}`);
        if (kind === 'phones') return parts.map((p) => formatPhone(p));
        return parts; // emails — shown as-is
    }, [output, kind, groupLookup]);

    const computePosition = () => {
        const style = anchoredPopoverStyle(ref.current, { flipBelow: 220, gap: 4 });
        if (style) setPopoverStyle(style);
    };

    const handleMouseEnter = () => { clearTimeout(hoverTimeout.current); computePosition(); setOpen(true); };
    const handleMouseLeave = () => { hoverTimeout.current = setTimeout(() => setOpen(false), 100); };

    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    useEffect(() => () => clearTimeout(hoverTimeout.current), []);

    // Off (or no output) → just the badge, no popover.
    if (!enabled || !output) return children;

    return (
        <span className="ap-device-cell" ref={ref} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{ display: 'inline-block' }}>
            {children}
            {open ? (
                <div className="ap-device-popover" style={popoverStyle}>
                    <div className="ap-device-popover__title">{label}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', padding: '6px 12px', fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-text-secondary, #6b7280)', borderBottom: '1px solid var(--color-border, #e5e7eb)' }}>
                        <span>Message After: <strong style={{ color: 'var(--color-text-body, #374151)' }}>{formatMsgAfter(output.event_time, timeUnit)}</strong></span>
                        {hasRepeat ? (
                            <span>Repeat: <strong style={{ color: 'var(--color-text-body, #374151)' }}>{formatRepeat(output)}</strong></span>
                        ) : null}
                    </div>
                    <div className="ap-device-popover__title" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>{listTitle} ({items.length})</div>
                    <ul className="ap-device-popover__list">
                        {items.length === 0 ? (
                            <li className="ap-device-popover__item"><span className="ap-device-popover__name">None</span></li>
                        ) : (
                            items.map((it, i) => (
                                <li key={i} className="ap-device-popover__item"><span className="ap-device-popover__name">{it}</span></li>
                            ))
                        )}
                    </ul>
                </div>
            ) : null}
        </span>
    );
};

// ---------------------------------------------------------------------------
// Row-level kebab menu
// ---------------------------------------------------------------------------
const CareflowRowActions = ({
    row,
    subTabKey,
    campusSettings,
    canManageCareflow = true,
    onEdit,
    onDelete,
    onAssignDevices,
    onAssignResidentGroups,
    onAssignAreaGroups,
    onAssignMobileGroups,
    onAssignPagerGroups,
    onAssignTextPhones,
    onAssignVoicePhones,
    onAssignEmails,
    onSetOffsiteDelay,
}) => {
    // Build conditional output menu items with per-action feather icons.
    // Mobile / Pager / Offsite are gated to canManageCareflow; Text / Voice /
    // Email remain available below that threshold so low-privilege users can
    // edit recipient lists for outputs that are already On.
    const conditionalItems = [];
    if (canManageCareflow && campusSettings?.app_active === 1 && row.outputs?.palmobile?.enabled) {
        conditionalItems.push({ label: 'Assign Mobile Groups', icon: 'fe-smartphone', handler: onAssignMobileGroups });
    }
    if (campusSettings?.text_messaging_active === 1 && row.outputs?.textmessages?.enabled) {
        conditionalItems.push({ label: 'Assign Text Phone #s', icon: 'fe-message-square', handler: onAssignTextPhones });
    }
    if (campusSettings?.voice_active === 1 && row.outputs?.voicemessages?.enabled) {
        conditionalItems.push({ label: 'Assign Voice Phone #s', icon: 'fe-phone', handler: onAssignVoicePhones });
    }
    if (campusSettings?.email_active === 1 && row.outputs?.email?.enabled) {
        conditionalItems.push({ label: 'Assign Emails', icon: 'fe-mail', handler: onAssignEmails });
    }
    if (canManageCareflow && campusSettings?.pager_active === 1 && row.outputs?.pager?.enabled) {
        conditionalItems.push({ label: 'Assign Pager Groups', icon: 'fe-radio', handler: onAssignPagerGroups });
    }
    if (canManageCareflow && campusSettings?.offsite_active === 1 && row.outputs?.offsitemonitoring?.enabled) {
        conditionalItems.push({ label: 'Set Offsite Delay', icon: 'fe-clock', handler: onSetOffsiteDelay });
    }

    // If a low-privilege user has no available actions on this row, hide the kebab.
    const hasAnyAction = canManageCareflow || conditionalItems.length > 0;
    if (!hasAnyAction) return null;

    return (
        <RowActionsMenu label={`Actions for ${row.careflow_description}`} classPrefix="ap">
            {(close) => (
                <>
                    {canManageCareflow ? (
                        <button type="button" onClick={() => { onEdit(row); close(); }} className="ap-row-actions__item">
                            <i className="fe fe-edit ap-row-actions__item-icon" aria-hidden="true" />
                            Edit Careflow
                        </button>
                    ) : null}
                    {canManageCareflow && subTabKey === 'device' ? (
                        <button type="button" onClick={() => { onAssignDevices(row); close(); }} className="ap-row-actions__item">
                            <i className="fe fe-cpu ap-row-actions__item-icon" aria-hidden="true" />
                            Careflow Devices
                        </button>
                    ) : null}
                    {canManageCareflow && subTabKey === 'resident' ? (
                        <button type="button" onClick={() => { onAssignResidentGroups(row); close(); }} className="ap-row-actions__item">
                            <i className="fe fe-users ap-row-actions__item-icon" aria-hidden="true" />
                            Careflow Resident Groups
                        </button>
                    ) : null}
                    {canManageCareflow && subTabKey === 'area' ? (
                        <button type="button" onClick={() => { onAssignAreaGroups(row); close(); }} className="ap-row-actions__item">
                            <i className="fe fe-map ap-row-actions__item-icon" aria-hidden="true" />
                            Careflow Area Groups
                        </button>
                    ) : null}
                    {conditionalItems.length > 0 ? (
                        <>
                            {canManageCareflow ? <div className="ap-row-actions__divider" /> : null}
                            {conditionalItems.map((item) => (
                                <button key={item.label} type="button" onClick={() => { item.handler(row); close(); }} className="ap-row-actions__item">
                                    <i className={`fe ${item.icon} ap-row-actions__item-icon`} aria-hidden="true" />
                                    {item.label}
                                </button>
                            ))}
                        </>
                    ) : null}
                    {canManageCareflow ? (
                        <>
                            <div className="ap-row-actions__divider" />
                            <button type="button" onClick={() => { onDelete([row.careflow_id]); close(); }} className="ap-row-actions__item ap-row-actions__item--danger">
                                <i className="fe fe-trash-2 ap-row-actions__item-icon" aria-hidden="true" />
                                Delete
                            </button>
                        </>
                    ) : null}
                </>
            )}
        </RowActionsMenu>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const CareflowTable = ({
    data,
    subTabKey,
    eventCategories,
    campusSettings,
    residentGroups,
    areaGroups,
    devices,
    mobileGroups,
    canManageCareflow = true,
    onAdd,
    onEdit,
    onDelete,
    onAssignDevices,
    onAssignResidentGroups,
    onAssignAreaGroups,
    onAssignMobileGroups,
    onAssignPagerGroups,
    onAssignTextPhones,
    onAssignVoicePhones,
    onAssignEmails,
    onSetOffsiteDelay,
    saving,
}) => {
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [sortColumn, setSortColumn] = useState('careflow_description');
    const [sortOrder, setSortOrder] = useState('asc');
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();

    // Build column defs based on subTabKey and global Output Settings
    const COLUMN_DEFS = useMemo(() => buildColumnDefs(subTabKey, campusSettings), [subTabKey, campusSettings]);

    const DEFAULT_VISIBLE = useMemo(() => COLUMN_DEFS.map((c) => c.key), [COLUMN_DEFS]);
    const ALL_COLUMN_KEYS = useMemo(() => COLUMN_DEFS.map((c) => c.key), [COLUMN_DEFS]);
    const [visibleColumns, handleColumnToggle] = usePersistedColumns(`settings-careflow-${subTabKey}`, DEFAULT_VISIBLE, ALL_COLUMN_KEYS);

    const COLUMN_LABELS = useMemo(() => {
        const map = {};
        COLUMN_DEFS.forEach((col) => { map[col.key] = col.label; });
        return map;
    }, [COLUMN_DEFS]);

    // Resolve mobile/pager group_id → group_name for the output badge popovers.
    const mobileGroupLookup = useMemo(() => {
        const m = new Map();
        (mobileGroups || []).forEach((g) => {
            if (g.group_id != null) m.set(String(g.group_id), g.group_name || `Group ${g.group_id}`);
        });
        return m;
    }, [mobileGroups]);

    // Clear selection on data reload
    useEffect(() => { setSelectedIds([]); }, [data]);

    // The references-column filter values are subtab-specific (device composites
    // vs resident/area group names), so drop any active references filter when
    // switching subtabs to avoid a carried-over filter matching nothing.
    useEffect(() => {
        setFilters((prev) => {
            if (!prev[REFERENCES_COLUMN_KEY]?.length) return prev;
            const next = { ...prev };
            delete next[REFERENCES_COLUMN_KEY];
            return next;
        });
    }, [subTabKey]);

    // ======================================================================
    // Data enrichment
    // ======================================================================
    const enrichedData = useMemo(() => {
        return (data || []).map((item) => {
            const start = item.enable_start || 0;
            const end = item.enable_end || 0;
            const isAllDay = start === 0 && end === 86400;

            // Look up category name for alert types
            let catName = '';
            if (subTabKey === 'alert') {
                const cat = (eventCategories || []).find(c => c.event_category_id === item.event_category_id);
                catName = cat?.event_category_name || '';
            }

            // Resolve references to entity objects based on subtab type
            const refIds = item.references ? item.references.split(',').filter(Boolean) : [];
            let refObjects = [];
            if (subTabKey === 'device') {
                refObjects = refIds
                    .map((id) => (devices || []).find((d) => String(d.device_id) === String(id)))
                    .filter(Boolean);
            } else if (subTabKey === 'resident') {
                refObjects = refIds
                    .map((id) => (residentGroups || []).find((g) => String(g.resident_group_id) === String(id)))
                    .filter(Boolean);
            } else if (subTabKey === 'area') {
                refObjects = refIds
                    .map((id) => (areaGroups || []).find((g) => String(g.area_group_id) === String(id)))
                    .filter(Boolean);
            }

            return {
                ...item,
                status_display: item.enabled === 1 ? 'Active' : 'Inactive',
                time_display: isAllDay ? 'All Day' : `${formatTime12h(start)} \u2013 ${formatTime12h(end)}`,
                event_category_display: catName,
                mobile_display: item.outputs?.palmobile?.enabled ? 'On' : 'Off',
                text_display: item.outputs?.textmessages?.enabled ? 'On' : 'Off',
                voice_display: item.outputs?.voicemessages?.enabled ? 'On' : 'Off',
                email_display: item.outputs?.email?.enabled ? 'On' : 'Off',
                pager_display: item.outputs?.pager?.enabled ? 'On' : 'Off',
                offsite_display: item.outputs?.offsitemonitoring?.enabled ? 'On' : 'Off',
                mute_display: item.outputs?.mute?.enabled ? 'On' : 'Off',
                references_count: refObjects.length,
                _refObjects: refObjects,
            };
        });
    }, [data, eventCategories, subTabKey, devices, residentGroups, areaGroups]);

    // Active columns based on visibility
    const activeColumns = useMemo(
        () => COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
        [COLUMN_DEFS, visibleColumns]
    );

    // Filter values for filter popovers
    const getFilterValues = useCallback((columnKey) => {
        const values = new Set();
        // References column: collect from each row's list of assigned entities
        // (devices / resident groups / area groups), not the scalar count.
        if (columnKey === REFERENCES_COLUMN_KEY) {
            enrichedData.forEach((row) => {
                (row._refObjects || []).forEach((item) => {
                    const v = referenceFilterValue(item, subTabKey);
                    if (v) values.add(v);
                });
            });
            return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
        }
        enrichedData.forEach((row) => {
            const val = row[columnKey];
            values.add(val != null && val !== '' ? String(val) : '(empty)');
        });
        return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    }, [enrichedData, subTabKey]);

    // ======================================================================
    // Data pipeline: Search -> Filters -> Sort
    // ======================================================================
    const filteredData = useMemo(() => {
        let d = [...enrichedData];

        // Search
        if (search.trim()) {
            const term = search.toLowerCase();
            d = d.filter((r) =>
                (r.careflow_description || '').toLowerCase().includes(term) ||
                (r.time_display || '').toLowerCase().includes(term) ||
                (r.event_category_display || '').toLowerCase().includes(term)
            );
        }

        // Column filters
        Object.entries(filters).forEach(([columnKey, values]) => {
            if (!values || values.length === 0) return;
            // References column: keep rows whose assigned entities include any
            // selected device/group (membership), not a scalar value match.
            if (columnKey === REFERENCES_COLUMN_KEY) {
                d = d.filter((row) =>
                    (row._refObjects || []).some((item) => values.includes(referenceFilterValue(item, subTabKey)))
                );
                return;
            }
            d = d.filter((row) => {
                const val = row[columnKey];
                const strVal = val != null && val !== '' ? String(val) : '(empty)';
                return values.includes(strVal);
            });
        });

        // Sort — use raw seconds for time column so display sorts correctly
        d.sort((a, b) => {
            let aVal, bVal;
            if (sortColumn === 'time_display') { aVal = a.enable_start ?? 0; bVal = b.enable_start ?? 0; }
            else { aVal = a[sortColumn] ?? ''; bVal = b[sortColumn] ?? ''; }
            const result = typeof aVal === 'number' && typeof bVal === 'number'
                ? aVal - bVal
                : String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
            return sortOrder === 'asc' ? result : -result;
        });

        return d;
    }, [enrichedData, search, filters, sortColumn, sortOrder, subTabKey]);

    // Reset page on filter/search change
    useEffect(() => { setPage(1); }, [search, filters, sortColumn, sortOrder]);

    // ======================================================================
    // Pagination
    // ======================================================================
    const totalFiltered = filteredData.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
    const safePage = Math.min(page, totalPages);
    const startIdx = (safePage - 1) * perPage;
    const endIdx = Math.min(startIdx + perPage, totalFiltered);
    const paginatedData = filteredData.slice(startIdx, endIdx);

    // ======================================================================
    // Selection
    // ======================================================================
    const allSelected = paginatedData.length > 0 && paginatedData.every((r) => selectedIds.includes(r.careflow_id));
    const someSelected = selectedIds.length > 0 && !allSelected;

    const handleSelectRow = useCallback((id) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    }, []);

    const handleSelectAll = useCallback(() => {
        const pageIds = paginatedData.map((r) => r.careflow_id);
        const currentAllSelected = pageIds.every((id) => selectedIds.includes(id));
        if (currentAllSelected) {
            setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
        } else {
            setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
        }
    }, [paginatedData, selectedIds]);

    // ======================================================================
    // Handlers
    // ======================================================================
    const handleSort = useCallback((column) => {
        if (sortColumn === column) {
            setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
    }, [sortColumn]);

    const handleFilterChange = useCallback((columnKey, values) => {
        setFilters((prev) => ({ ...prev, [columnKey]: values }));
    }, []);

    const handleRemoveFilter = useCallback((columnKey, value) => {
        setFilters((prev) => ({ ...prev, [columnKey]: (prev[columnKey] || []).filter((v) => v !== value) }));
    }, []);

    const handleClearAllFilters = useCallback(() => { setFilters({}); setSearch(''); }, []);

    const handleExportAll = useCallback(() => {
        if (filteredData.length) exportCareflowCsv(filteredData, 'careflows.csv', campusSettings);
    }, [filteredData, campusSettings]);

    const handleBulkExport = useCallback(() => {
        const selected = enrichedData.filter((r) => selectedIds.includes(r.careflow_id));
        if (selected.length) exportCareflowCsv(selected, 'careflows-selected.csv', campusSettings);
    }, [selectedIds, enrichedData, campusSettings]);

    const handleBulkDelete = () => {
        if (selectedIds.length === 0) return;
        onDelete(selectedIds);
    };

    // ======================================================================
    // Sort icons
    // ======================================================================
    const renderSortIcon = (columnKey) => {
        if (sortColumn !== columnKey) {
            return (
                <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
                </svg>
            );
        }
        return sortOrder === 'asc' ? (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
            </svg>
        ) : (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
        );
    };

    // ======================================================================
    // Custom cell rendering
    // ======================================================================
    // Lookup table for output-badge click handlers, keyed by handler name in
    // OUTPUT_BADGE_HANDLER_KEYS so renderCell can dispatch by column key.
    const handlers = {
        onAssignMobileGroups,
        onAssignTextPhones,
        onAssignVoicePhones,
        onAssignEmails,
        onAssignPagerGroups,
        onSetOffsiteDelay,
    };

    const renderCell = (row, columnKey) => {
        switch (columnKey) {
            case 'status_display': {
                const isActive = row.status_display === 'Active';
                return (
                    <span className={`ws-status ${isActive ? 'ws-status--active' : 'ws-status--inactive'}`}>
                        <span className="ws-status__dot" aria-hidden="true" />
                        {row.status_display}
                    </span>
                );
            }
            case 'references_count': {
                // Match the kebab's assign-action wiring: device → onAssignDevices,
                // resident → onAssignResidentGroups, area → onAssignAreaGroups.
                // canEdit mirrors the kebab's canManageCareflow gate, so low-priv
                // users still hover-preview but the click is a no-op.
                let assignHandler = null;
                if (subTabKey === 'device') assignHandler = onAssignDevices;
                else if (subTabKey === 'resident') assignHandler = onAssignResidentGroups;
                else if (subTabKey === 'area') assignHandler = onAssignAreaGroups;
                return (
                    <ReferencesPopoverCell
                        items={row._refObjects}
                        subTabKey={subTabKey}
                        row={row}
                        canEdit={canManageCareflow}
                        onAssign={assignHandler}
                    />
                );
            }
            case 'mobile_display':
            case 'text_display':
            case 'voice_display':
            case 'email_display':
            case 'pager_display':
            case 'offsite_display': {
                const isOn = row[columnKey] === 'On';
                const handlerName = OUTPUT_BADGE_HANDLER_KEYS[columnKey];
                const handler = handlers[handlerName];
                const label = COLUMN_LABELS[columnKey] || '';
                // Low-priv users may only edit text/voice/email lists for outputs
                // already On — no enabling Off badges, no mobile/pager/offsite.
                const lowPrivAllowed = isOn && (
                    columnKey === 'text_display' ||
                    columnKey === 'voice_display' ||
                    columnKey === 'email_display'
                );
                const isClickable = canManageCareflow || lowPrivAllowed;
                const popCfg = OUTPUT_POPOVER_CONFIG[columnKey];
                // A recipient-based output (mobile/text/voice/email/pager) that is On
                // but has no recipients is misconfigured — flag it amber so it's
                // visually distinct from a working On. Offsite has no popCfg/recipient
                // list, so it never warns.
                const outputRec = popCfg ? row.outputs?.[popCfg.outputKey] : null;
                const hasRecipients = !!(outputRec?.reference && String(outputRec.reference).split(',').filter(Boolean).length);
                const isMisconfigured = isOn && !!popCfg && !hasRecipients;
                const stateClass = isMisconfigured
                    ? 'ws-status--warning'
                    : isOn ? 'ws-status--active' : 'ws-status--inactive';

                let badgeEl;
                if (!isClickable) {
                    badgeEl = (
                        <span className={`ws-status ${stateClass}`}>
                            <span className="ws-status__dot" aria-hidden="true" />
                            {row[columnKey]}
                        </span>
                    );
                } else {
                    const tooltip = isMisconfigured
                        ? `${label} is On but has no recipients — click to add`
                        : isOn
                            ? `Click to edit ${label} assignments`
                            : `Click to enable ${label} and add assignments`;
                    badgeEl = (
                        <button
                            type="button"
                            onClick={() => handler && handler(row)}
                            className={`ws-status ws-status--clickable ${stateClass}`}
                            aria-label={tooltip}
                        >
                            <span className="ws-status__dot" aria-hidden="true" />
                            {row[columnKey]}
                        </button>
                    );
                }

                // Offsite has no config — render the badge unchanged. Mobile / Text /
                // Voice / Email / Pager get a hover popover when On.
                if (!popCfg) return badgeEl;
                return (
                    <OutputBadgePopover
                        enabled={isOn}
                        output={row.outputs?.[popCfg.outputKey]}
                        kind={popCfg.kind}
                        timeUnit={popCfg.timeUnit}
                        hasRepeat={popCfg.hasRepeat}
                        listTitle={popCfg.listTitle}
                        label={label}
                        groupLookup={mobileGroupLookup}
                    >
                        {badgeEl}
                    </OutputBadgePopover>
                );
            }
            case 'mute_display': {
                const isOn = row[columnKey] === 'On';
                return (
                    <span className={`ws-status ${isOn ? 'ws-status--active' : 'ws-status--inactive'}`}>
                        <span className="ws-status__dot" aria-hidden="true" />
                        {row[columnKey]}
                    </span>
                );
            }
            default:
                return <span className="ap-cell-text">{row[columnKey] != null ? row[columnKey] : '\u2014'}</span>;
        }
    };

    // ======================================================================
    // Render
    // ======================================================================
    const hasFilters = search.trim() || Object.values(filters).some((v) => v.length > 0);

    return (
        <>
            {/* Controls row */}
            <div className="ap-controls">
                <div className="ap-controls__left">
                    <div className="ap-search">
                        <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                        <input type="search" placeholder="Search careflows..." value={search} onChange={(e) => setSearch(e.target.value)} className="ap-search__input" aria-label="Search careflows" />
                    </div>
                </div>
                <div className="ap-controls__right">
                    <ColumnVisibilityToggle columns={COLUMN_DEFS} visibleColumns={visibleColumns} onToggle={handleColumnToggle} />
                    <button type="button" className="ap-btn ap-btn--secondary" onClick={handleExportAll} aria-label="Export careflows">
                        <svg className="ap-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                        </svg>
                        Export
                    </button>
                    {canManageCareflow ? (
                        <button type="button" onClick={onAdd} className="ap-btn ap-btn--primary" aria-label="Add careflow">
                            <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                            Add Careflow
                        </button>
                    ) : null}
                </div>
            </div>

            {/* Active filter chips */}
            <ActiveFilterBar filters={filters} columnLabels={COLUMN_LABELS} onRemoveFilter={handleRemoveFilter} onClearAll={handleClearAllFilters} />

            {/* Bulk action bar */}
            {selectedIds.length > 0 ? (
                <div className="ap-bulk-bar">
                    <div className="ap-bulk-bar__left">
                        <span className="ap-bulk-bar__count">{selectedIds.length} careflow{selectedIds.length !== 1 ? 's' : ''} selected</span>
                        <button type="button" onClick={() => setSelectedIds([])} className="ap-bulk-bar__deselect">Deselect All</button>
                    </div>
                    <div className="ap-bulk-bar__right">
                        <button type="button" onClick={handleBulkExport} className="ap-bulk-btn" aria-label="Export selected">
                            <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                            </svg>
                            Export ({selectedIds.length})
                        </button>
                        {canManageCareflow ? (
                            <button type="button" onClick={handleBulkDelete} disabled={saving} className="ap-bulk-btn ap-bulk-btn--danger" aria-label="Delete selected">
                                <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                </svg>
                                Delete ({selectedIds.length})
                            </button>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {/* Table */}
            {filteredData.length === 0 ? (
                <div className="ss-empty" style={{ marginTop: 'var(--space-4)' }}>
                    <p className="ss-empty__text">
                        {hasFilters ? 'No careflows match your filters.' : 'No careflows configured.'}
                    </p>
                </div>
            ) : (
                <>
                    <div className="ap-table-wrap">
                        <table className="ap-table">
                            <thead className="ap-thead">
                                <tr>
                                    <th scope="col" className="ap-th ap-th--checkbox">
                                        <input type="checkbox" checked={allSelected} ref={(el) => { if (el) el.indeterminate = someSelected; }} onChange={handleSelectAll} className="ap-checkbox" aria-label="Select all" />
                                    </th>
                                    <th scope="col" className="ap-th ap-th--actions">Edit</th>
                                    {activeColumns.map((col) => (
                                        <th key={col.key} scope="col" className="ap-th" aria-sort={col.sortable ? ariaSort(sortColumn, sortOrder, col.key) : undefined}>
                                            <div className="ap-th__content">
                                                {col.sortable ? (
                                                    <button type="button" onClick={() => handleSort(col.key)} className="ap-th__sort-btn" aria-label={`Sort by ${col.label}`}>
                                                        {col.label}
                                                        {renderSortIcon(col.key)}
                                                    </button>
                                                ) : <span>{col.label}</span>}
                                                {col.filterable ? (
                                                    <FilterPopover columnKey={col.key} label={col.label} values={getFilterValues(col.key)} selectedValues={filters[col.key] || []} onChange={handleFilterChange} wide={col.key === REFERENCES_COLUMN_KEY} />
                                                ) : null}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="ap-tbody">
                                {paginatedData.map((row) => {
                                    const isSelected = selectedIds.includes(row.careflow_id);
                                    return (
                                        <tr key={row.careflow_id} className={`ap-tr${isSelected ? ' is-selected' : ''}`}>
                                            <td className="ap-td ap-td--checkbox">
                                                <input type="checkbox" checked={isSelected} onChange={() => handleSelectRow(row.careflow_id)} className="ap-checkbox" aria-label={`Select ${row.careflow_description}`} />
                                            </td>
                                            <td className="ap-td ap-td--actions">
                                                <CareflowRowActions
                                                    row={row}
                                                    subTabKey={subTabKey}
                                                    campusSettings={campusSettings}
                                                    canManageCareflow={canManageCareflow}
                                                    onEdit={onEdit}
                                                    onDelete={onDelete}
                                                    onAssignDevices={onAssignDevices}
                                                    onAssignResidentGroups={onAssignResidentGroups}
                                                    onAssignAreaGroups={onAssignAreaGroups}
                                                    onAssignMobileGroups={onAssignMobileGroups}
                                                    onAssignPagerGroups={onAssignPagerGroups}
                                                    onAssignTextPhones={onAssignTextPhones}
                                                    onAssignVoicePhones={onAssignVoicePhones}
                                                    onAssignEmails={onAssignEmails}
                                                    onSetOffsiteDelay={onSetOffsiteDelay}
                                                />
                                            </td>
                                            {activeColumns.map((col) => (
                                                <td key={col.key} className="ap-td">
                                                    {renderCell(row, col.key)}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <Pagination
                        page={safePage}
                        totalPages={totalPages}
                        total={totalFiltered}
                        perPage={perPage}
                        onPageChange={setPage}
                        onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                        entityLabel={totalFiltered === 1 ? 'careflow' : 'careflows'}
                        selectId="careflow-per-page"
                    />
                </>
            )}
        </>
    );
};

export default CareflowTable;
